import { useCallback, useContext, useMemo } from 'react'
import type { ComponentProps, JSX, ReactNode } from 'react'
import { clsx } from 'clsx'
import { Checkbox, HelpButton, ToggleButton } from '../../../../components'
import type { FieldBlockProps, FieldBlockWidth } from '../../FieldBlock'
import FieldBlock from '../../FieldBlock'
import { useFieldProps } from '../../hooks'
import type { ReturnAdditional } from '../../hooks/useFieldProps'
import { checkForError } from '../../hooks/useFieldProps'
import type { DefaultErrorMessages, FieldProps, Path } from '../../types'
import { pickSpacingProps } from '../../../../components/flex/utils'
import ToggleButtonGroupContext from '../../../../components/toggle-button/ToggleButtonGroupContext'
import type { HelpProps } from '../../../../components/help-button/HelpButtonInline'
import type { Data } from '../Selection'
import { countOptions, mapOptions } from '../Selection'
import DataContext from '../../DataContext/Context'
import useDataValue from '../../hooks/useDataValue'
import type { FormError } from '../../utils'
import type { CheckboxProps } from '../../../../components/Checkbox'
import type { ToggleButtonProps } from '../../../../components/ToggleButton'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

type OptionProps = ComponentProps<
  (props: {
    value: number | string
    error: Error | FormError | undefined
    title: ReactNode
    help: HelpProps
    className: string
    children: ReactNode
    handleSelect: () => void
    size?: ToggleButtonProps['size'] | CheckboxProps['size']
  }) => JSX.Element
>

type OptionValue = string | number
type RenderArraySelectionChildren = (params: {
  value: FieldArraySelectionProps['value']
  options: FieldArraySelectionProps['data']
}) => ReactNode

export type FieldArraySelectionProps = FieldProps<
  Array<OptionValue> | undefined
> & {
  children?: ReactNode | RenderArraySelectionChildren
  variant?: 'checkbox' | 'button' | 'checkbox-button'
  optionsLayout?: 'horizontal' | 'vertical'

  /**
   * The width of the component.
   */
  width?: FieldBlockWidth

  /**
   * The path to the context data (Form.Handler). The context data object needs to have a `value` and a `title` property. The generated options will be placed above given JSX based children. When `children` is a function, the generated options are instead provided as `options` to the function.
   */
  dataPath?: Path

  /**
   * Data to be used for the component. The object needs to have a `value` and a `title` property. Provide the Dropdown or Autocomplete data in the format documented here: [Dropdown](/uilib/components/dropdown) and [Autocomplete](/uilib/components/autocomplete) documentation.
   */
  data?: Data

  /**
   * The sizes you can choose are `small` (1.5rem), `default` (2rem), `medium` (2.5rem) and `large` (3rem). Defaults to `default` / `null`. Also, if you define a number like `size="2"` then it will be forwarded as the input element attribute. Consider rather setting field sizes with [Form.Appearance](/uilib/extensions/forms/Form/Appearance/).
   */
  size?: ToggleButtonProps['size'] | CheckboxProps['size']

  errorMessages?: DefaultErrorMessages & {
    minItems?: string
    maxItems?: string
  }
}

function ArraySelection(props: FieldArraySelectionProps) {
  const {
    id,
    path,
    dataPath,
    data,
    className,
    variant = 'checkbox',
    layout = 'vertical',
    optionsLayout = 'vertical',
    value,
    hasError,
    info,
    warning,
    disabled,
    size,
    width,
    emptyValue,
    htmlAttributes,
    handleChange,
    setDisplayValue,
    children,
    label,
  } = useFieldProps(props)

  const { value: dataPathValue } = useDataValue(dataPath, undefined, {
    pathType: 'absolute',
  })
  const dataList = dataPath ? dataPathValue : data
  const hasRenderPropChildren = typeof children === 'function'
  const renderedChildren = useMemo(() => {
    return resolveChildren(children, value, dataList)
  }, [children, dataList, value])

  const fieldBlockProps: FieldBlockProps = {
    forId: id,
    className: clsx(
      'dnb-forms-field-array-selection',
      `dnb-forms-field-array-selection--variant-${
        variant === 'checkbox' ? 'checkbox' : 'button'
      }`,
      `dnb-forms-field-array-selection--layout-${layout}`,
      `dnb-forms-field-array-selection--options-layout--${optionsLayout}`,
      className
    ),
    contentClassName: 'dnb-forms-field-array-selection__options',
    disableStatusSummary: true,
    asFieldset: Boolean(label),
    fieldsetRole: 'group',
    ...pickSpacingProps(props),
  }
  if (!size) {
    fieldBlockProps.labelHeight = 'small'
  }
  if (width) {
    if (width === 'stretch') {
      fieldBlockProps.width = width
    }
    fieldBlockProps.contentWidth = width
  }

  const options = useCheckboxOrToggleOptions({
    id,
    path,
    variant,
    info,
    warning,
    emptyValue,
    htmlAttributes,
    dataList: hasRenderPropChildren ? undefined : dataList,
    children: renderedChildren,
    value,
    disabled,
    size,
    hasError,
    handleChange,
    handleActiveData: ({ labels }) => {
      setDisplayValue(labels)
    },
  })

  switch (variant) {
    case 'checkbox':
      return <FieldBlock {...fieldBlockProps}>{options}</FieldBlock>
    default:
      return (
        <FieldBlock {...fieldBlockProps}>
          <ToggleButtonGroupContext
            value={{
              status: hasError ? 'error' : undefined,
              disabled,
              size,
            }}
          >
            {options}
          </ToggleButtonGroupContext>
        </FieldBlock>
      )
  }
}

function resolveChildren(
  children: FieldArraySelectionProps['children'],
  value: FieldArraySelectionProps['value'],
  options: FieldArraySelectionProps['data']
) {
  if (typeof children === 'function') {
    return children({ value, options })
  }

  return children
}

export function useCheckboxOrToggleOptions({
  id,
  path,
  variant = 'checkbox',
  info,
  warning,
  emptyValue,
  htmlAttributes,
  dataList,
  children,
  value,
  disabled,
  size,
  hasError,
  handleChange,
  handleActiveData,
}: {
  id: FieldArraySelectionProps['id']
  path?: FieldArraySelectionProps['path']
  variant?: FieldArraySelectionProps['variant']
  info?: FieldArraySelectionProps['info']
  warning?: FieldArraySelectionProps['warning']
  emptyValue?: FieldArraySelectionProps['emptyValue']
  htmlAttributes?: FieldArraySelectionProps['htmlAttributes']
  dataList?: FieldArraySelectionProps['data']
  children?: ReactNode
  value?: FieldArraySelectionProps['value']
  disabled?: FieldArraySelectionProps['disabled']
  size?: FieldArraySelectionProps['size']
  hasError?: ReturnAdditional<
    FieldArraySelectionProps['value']
  >['hasError']
  handleChange?: ReturnAdditional<
    FieldArraySelectionProps['value']
  >['handleChange']
  handleActiveData?: (item: { labels: ReactNode[] }) => void
}) {
  const { setFieldInternals } = useContext(DataContext)
  const optionsCount = useMemo(
    () => countOptions(children) + (dataList?.length || 0),
    [dataList, children]
  )
  const activeData: Array<OptionProps> = []

  const createOption = useCallback(
    (props: OptionProps, i: number) => {
      const {
        value: active,
        error,
        title,
        help,
        className,
        children,
        ...rest
      } = props

      if (value?.includes(active)) {
        activeData.push(props)
      }

      const label = title ?? children
      const suffix = help ? (
        <HelpButton size="small" title={help.title}>
          {help.content}
        </HelpButton>
      ) : undefined

      const handleSelect = () => {
        const newValue = value?.includes(active)
          ? value.filter((value) => value !== active)
          : [...(value ?? []), active]

        handleChange?.(
          newValue.length === 0 ? (emptyValue as typeof value) : newValue
        )
      }

      const Component = (
        variant === 'checkbox' ? Checkbox : ToggleButton
      ) as typeof Checkbox & typeof ToggleButton

      return (
        <Component
          id={optionsCount === 1 ? id : undefined}
          key={`option-${i}`}
          variant={variant === 'checkbox-button' ? 'checkbox' : undefined}
          className={clsx(
            `dnb-forms-field-array-selection__${
              variant === 'checkbox' ? 'checkbox' : 'button'
            }`,
            className
          )}
          size={size}
          label={variant === 'checkbox' ? label : undefined}
          text={variant !== 'checkbox' ? label : undefined}
          value={value}
          disabled={disabled}
          checked={value?.includes(active)}
          status={
            hasError || checkForError([error, info, warning])
              ? 'error'
              : undefined
          }
          suffix={suffix}
          role="checkbox"
          onChange={handleSelect}
          {...htmlAttributes}
          {...rest}
        />
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      disabled,
      emptyValue,
      handleChange,
      hasError,
      htmlAttributes,
      id,
      info,
      optionsCount,
      size,
      value,
      variant,
      warning,
    ]
  )

  const result = [
    ...(dataList || []).map((props, i) =>
      createOption(props as OptionProps, i)
    ),
    ...(mapOptions(children, { createOption }) || []).filter(Boolean),
  ]

  if (handleActiveData) {
    handleActiveData({
      labels: activeData.map(({ title, children }) => title ?? children),
    })
  }

  if (path) {
    setFieldInternals?.(path + '/arraySelectionData', {
      props: activeData,
    })
  }

  return result
}

withComponentMarkers(ArraySelection, {
  _supportsSpacingProps: true,
})

export default ArraySelection
